/**
 * deriveTransparencyTurns — the ONE derivation of live transparency steps
 * from the raw `messages: StreamMessage[]` array (bead
 * terra-design-v2-chat-yub.8, p1.s7). Reads the same `messages` DSRP
 * already holds; produces both:
 *
 *  - `stepsMap` (`TransparencyStepsContext`'s existing bare-map shape,
 *    `Record<string, TransparencyStep[]>`) — unchanged consumer contract,
 *    extended with two ADDITIVE aliases (status-row, and turn-associated
 *    assistant) so `ChatReasoningTrace` (V1) gets live steps for free.
 *  - `turnsMap` (`Record<string, TransparencyTurn>`) — the NEW per-turn
 *    record the sibling `TransparencyTurnsContext` exposes for V2's
 *    `ChatRunSteps` turn placeholder (bead .9).
 *
 * See the bead's "Behavior Specifications (from Codex)" section (points
 * 1-15) for the full contract this implements. Key invariants:
 *  - A frame without a usable `turnId` cannot create or update a turn
 *    record (point 3) — never infer association from proximity.
 *  - Generation precedence: compare `turnEpoch` first, then `attempt`
 *    within the same epoch (point 4). A higher generation REPLACES the
 *    turn's steps/status/assistant association; a lower generation is
 *    ignored; a frame with neither coordinate belongs to the CURRENT
 *    generation (never signals a retry) and never clears a known
 *    coordinate.
 *  - `steps_update` REPLACES the steps array wholesale — never
 *    concatenates, never selects by length (point 5).
 *  - The final assistant's `stats.steps`, once present, is authoritative
 *    and cannot be overridden by a later same-generation snapshot (point
 *    11) — tracked via `finalLocked`.
 *  - The legacy "any assistant message with `stats.steps`" -> stepsMap
 *    mapping is preserved verbatim, independent of turnId, so existing
 *    (pre-turn) callers/tests keep working unmodified (Do-Not-Create: "No
 *    removal of the existing stats.steps path").
 */
import type { StreamMessage } from '../../../types/stream-protocol';
import type { TransparencyStep } from '../../../types/transparency';
import type { StepsMap, TurnsMap } from '../../shared/TransparencyStepsContext';

function visibleSteps(steps: TransparencyStep[]): TransparencyStep[] {
  return steps.filter((s) => s.tier <= 2);
}

interface Generation {
  turnEpoch?: number;
  attempt?: number;
}

/**
 * Compares two generations. Returns positive when `incoming` is strictly
 * higher than `current`, negative when strictly lower, 0 when the same or
 * when there isn't enough information on both sides to compare (in which
 * case the frame belongs to the current generation — point 4's null
 * handling).
 */
function compareGeneration(current: Generation, incoming: Generation): number {
  if (incoming.turnEpoch === undefined && incoming.attempt === undefined) return 0;
  if (current.turnEpoch !== undefined && incoming.turnEpoch !== undefined) {
    if (incoming.turnEpoch !== current.turnEpoch) {
      return incoming.turnEpoch > current.turnEpoch ? 1 : -1;
    }
  }
  if (current.attempt !== undefined && incoming.attempt !== undefined) {
    if (incoming.attempt !== current.attempt) {
      return incoming.attempt > current.attempt ? 1 : -1;
    }
  }
  return 0;
}

interface InternalTurn {
  turnId: string;
  attempt?: number;
  turnEpoch?: number;
  steps: TransparencyStep[];
  latestStatus: string | null;
  assistantMessageId?: string;
  /** messageId of the latest surviving status row for this turn/generation
   *  (point 9's stepsMap alias), cleared once an assistant associates. */
  latestStatusMessageId?: string;
  /** Set once the final assistant's stats.steps has been applied for the
   *  current generation — subsequent same-generation steps_update frames
   *  must not override it (point 11). Reset on generation advance. */
  finalLocked: boolean;
  /** True once an `error` frame carrying this turn's turnId has arrived
   *  (bead terra-design-v2-chat-yub.9 — additive beyond bead .8's original
   *  field list: the turn record has no other way to surface a failed run,
   *  since the 'error' StreamMessage converts to its OWN ThreadMessageLike
   *  row and never replaces the never-finalized assistant placeholder. V2's
   *  `ChatRunSteps` needs this to render "Failed · N steps" instead of
   *  merely "Stopped · N steps"). Reset on generation advance, same as
   *  every other per-generation field. */
  errored: boolean;
  /** bead terra-design-v2-chat-yub.9: `useConversationStream.ts`'s synthetic
   *  in-progress assistant message (built client-side from `assistant_delta`
   *  frames) carries no turnId — a known, flagged gap in a file read-only to
   *  this epic (see that bead's Implementation Notes). Attributed below, by
   *  elimination, to the single turn still awaiting its own turnId-driven
   *  assistant association. Cleared once that real association lands. */
  provisionalAssistantMessageId?: string;
}

function getOrCreateTurn(turns: Map<string, InternalTurn>, turnId: string): InternalTurn {
  let turn = turns.get(turnId);
  if (!turn) {
    turn = {
      turnId,
      steps: [],
      latestStatus: null,
      finalLocked: false,
      errored: false,
    };
    turns.set(turnId, turn);
  }
  return turn;
}

/**
 * Applies generation precedence for an incoming frame against a turn's
 * current selected generation. Returns true when the frame is STALE (lower
 * generation) and must be ignored entirely.
 */
function applyGeneration(turn: InternalTurn, incoming: Generation): boolean {
  const cmp = compareGeneration({ turnEpoch: turn.turnEpoch, attempt: turn.attempt }, incoming);
  if (cmp < 0) return true; // stale — never overwrite the selected generation
  if (cmp > 0) {
    // Higher generation: a retry REPLACES this turn's steps/status/
    // assistant association — never appends.
    turn.turnEpoch = incoming.turnEpoch;
    turn.attempt = incoming.attempt;
    turn.steps = [];
    turn.latestStatus = null;
    turn.assistantMessageId = undefined;
    turn.latestStatusMessageId = undefined;
    turn.finalLocked = false;
    turn.errored = false;
    turn.provisionalAssistantMessageId = undefined;
    return false;
  }
  // Same generation — retain known coordinates, adopt any newly-known one.
  // Absent coordinates never themselves signal a retry (point 4).
  if (turn.turnEpoch === undefined && incoming.turnEpoch !== undefined) {
    turn.turnEpoch = incoming.turnEpoch;
  }
  if (turn.attempt === undefined && incoming.attempt !== undefined) {
    turn.attempt = incoming.attempt;
  }
  return false;
}

export function deriveTransparencyTurns(messages: StreamMessage[]): {
  stepsMap: StepsMap;
  turnsMap: TurnsMap;
} {
  const stepsMap: StepsMap = {};

  // Legacy pass — preserved verbatim, independent of turnId. This is the
  // mapping DurableStreamRuntimeProvider built before this bead; removing
  // it would be removing the existing stats.steps path (Do-Not-Create).
  for (const msg of messages) {
    if (msg.type === 'assistant' && msg.stats?.steps) {
      const filtered = visibleSteps(msg.stats.steps);
      if (filtered.length > 0) {
        stepsMap[msg.messageId] = filtered;
      }
    }
  }

  // Turn-keyed pass — only frames carrying an explicit, nonempty turnId
  // participate (point 3's null handling).
  const turns = new Map<string, InternalTurn>();

  for (const msg of messages) {
    const turnId = 'turnId' in msg ? msg.turnId : undefined;
    if (!turnId) continue;

    const turn = getOrCreateTurn(turns, turnId);
    const incomingGen: Generation = {
      turnEpoch: 'turnEpoch' in msg ? msg.turnEpoch : undefined,
      attempt: 'attempt' in msg ? msg.attempt : undefined,
    };
    const stale = applyGeneration(turn, incomingGen);
    if (stale) continue;

    switch (msg.type) {
      case 'steps_update':
        if (!turn.finalLocked) {
          turn.steps = visibleSteps(msg.steps);
        }
        break;
      case 'status':
        turn.latestStatus = msg.message;
        turn.latestStatusMessageId = msg.messageId;
        break;
      case 'turn_done':
        // End-of-turn control frame (added to the shared schema on master).
        // `useConversationStream` consumes it and returns without appending
        // (`useConversationStream.ts:289-296`), so in practice it never reaches
        // this reducer — this case is defensive, matching how `metadata` and
        // `turn_superseded` are handled in `DurableStreamRuntimeProvider`.
        // If it ever does arrive, the turn is over: drop the live status so a
        // finished run cannot keep showing a stale "Listing Gong calls…".
        // Steps are left alone — `assistant.stats.steps` is the final word and
        // may already have locked them.
        turn.latestStatus = null;
        turn.latestStatusMessageId = undefined;
        break;
      case 'assistant':
        if (msg.stats?.steps) {
          turn.steps = visibleSteps(msg.stats.steps);
          turn.finalLocked = true;
        }
        turn.assistantMessageId = msg.messageId;
        turn.latestStatusMessageId = undefined;
        break;
      case 'error':
        // bead terra-design-v2-chat-yub.9: an error frame for this
        // generation marks the turn errored — see InternalTurn.errored's
        // doc comment for why no other field carries this.
        turn.errored = true;
        break;
      case 'user':
      case 'metadata':
      case 'assistant_delta':
      case 'turn_superseded':
        // These frame types carrying a turnId still establish/advance the
        // generation above (via `applyGeneration`), but have no further
        // per-turn field to apply. `turn_superseded` specifically is
        // consumed by `useConversationStream` and never appended to the
        // messages array this function receives in practice (see
        // `DurableStreamRuntimeProvider.tsx`'s matching no-op case) — this
        // arm is a defensive no-op, not a behavior change.
        break;
    }
  }

  // bead terra-design-v2-chat-yub.9: attribute `useConversationStream.ts`'s
  // turnId-less synthetic in-progress assistant message (built from
  // assistant_delta frames — see InternalTurn.provisionalAssistantMessageId's
  // doc comment) to the single turn still awaiting its own turnId-driven
  // assistant association. Only when exactly one turn is open: conversations
  // process asks serially, so more than one open turn means this heuristic
  // can't safely guess which one the synthetic row belongs to, and zero open
  // turns means there is nothing left to attribute it to.
  let provisionalAssistantMessageId: string | undefined;
  for (const msg of messages) {
    if (msg.type === 'assistant' && !msg.stats && !('turnId' in msg && msg.turnId)) {
      provisionalAssistantMessageId = msg.messageId;
    }
  }
  if (provisionalAssistantMessageId) {
    const openTurns = [...turns.values()].filter((t) => !t.assistantMessageId);
    if (openTurns.length === 1) {
      openTurns[0].provisionalAssistantMessageId = provisionalAssistantMessageId;
    }
  }

  const turnsMap: TurnsMap = {};
  for (const [turnId, turn] of turns) {
    turnsMap[turnId] = {
      turnId: turn.turnId,
      attempt: turn.attempt,
      turnEpoch: turn.turnEpoch,
      steps: turn.steps,
      latestStatus: turn.latestStatus,
      assistantMessageId: turn.assistantMessageId,
      errored: turn.errored,
      provisionalAssistantMessageId: turn.provisionalAssistantMessageId,
    };

    if (turn.assistantMessageId) {
      stepsMap[turn.assistantMessageId] = turn.steps;
    } else if (turn.latestStatusMessageId) {
      stepsMap[turn.latestStatusMessageId] = turn.steps;
    }
  }

  return { stepsMap, turnsMap };
}
